import { ChangeDetectionStrategy, Component, computed, inject } from '@angular/core';
import { Router } from '@angular/router';
import {
  DashboardCardComponent,
  ProgressBarComponent,
} from '@catch/general-ui';
import { FontAwesomeModule } from '@fortawesome/angular-fontawesome';
import {
  faCheckCircle,
  faChevronRight,
  faCircle,
  faListCheck,
} from '@fortawesome/pro-light-svg-icons';
import { TranslocoPipe } from '@jsverse/transloco';
import { PlatformDocumentKind } from '../../../../enums/platform-document-kind.enum';
import { PlatformProfile } from '../../../../models/platform.models';
import { PlatformProfileService } from '../../../../services/platform-profile.service';

enum ProfileCompletenessFragment {
  DOCUMENTS = 'documents',
  SKILLS = 'skills',
  PREFERENCES = 'preferences',
}

interface ProfileCompletenessStep {
  titleKey: string;
  descriptionKey: string;
  fragment: ProfileCompletenessFragment;
}

interface ProfileCompletenessStepView extends ProfileCompletenessStep {
  completed: boolean;
}

const PROFILE_STEPS: ProfileCompletenessStep[] = [
  {
    titleKey: 'jobboard.platform.dashboard.profile-completeness.steps.cv.title',
    descriptionKey:
      'jobboard.platform.dashboard.profile-completeness.steps.cv.description',
    fragment: ProfileCompletenessFragment.DOCUMENTS,
  },
  {
    titleKey:
      'jobboard.platform.dashboard.profile-completeness.steps.qualifications.title',
    descriptionKey:
      'jobboard.platform.dashboard.profile-completeness.steps.qualifications.description',
    fragment: ProfileCompletenessFragment.SKILLS,
  },
  {
    titleKey:
      'jobboard.platform.dashboard.profile-completeness.steps.preferences.title',
    descriptionKey:
      'jobboard.platform.dashboard.profile-completeness.steps.preferences.description',
    fragment: ProfileCompletenessFragment.PREFERENCES,
  },
];

@Component({
  selector: 'app-platform-dashboard-profile-completeness',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    DashboardCardComponent,
    ProgressBarComponent,
    FontAwesomeModule,
    TranslocoPipe,
  ],
  templateUrl: './platform-dashboard-profile-completeness.component.html',
  styleUrl: './platform-dashboard-profile-completeness.component.scss',
})
export class PlatformDashboardProfileCompletenessComponent {
  private readonly router = inject(Router);
  private readonly profile = inject(PlatformProfileService);

  readonly icons = {
    listCheck: faListCheck,
    circle: faCircle,
    checkCircle: faCheckCircle,
    chevronRight: faChevronRight,
  };
  readonly progressPercent = this.profile.completenessPercent;

  readonly steps = computed<ProfileCompletenessStepView[]>(() => {
    const profile = this.profile.profile();
    return PROFILE_STEPS.map((step) => ({
      ...step,
      completed: this.isStepCompleted(step.fragment, profile),
    }));
  });

  /**
   * Navigates to the profile view, optionally to a section anchor.
   * @param fragment - Optional profile section id
   */
  openProfile(fragment?: string): void {
    void this.router.navigate(['/profile'], fragment ? { fragment } : undefined);
  }

  /**
   * Whether a completeness step is done for the current profile.
   * @param fragment - Profile section the step maps to
   * @param profile - Current profile snapshot
   * @returns True when the step's required data is present
   */
  private isStepCompleted(
    fragment: ProfileCompletenessFragment,
    profile: PlatformProfile
  ): boolean {
    switch (fragment) {
      case ProfileCompletenessFragment.DOCUMENTS:
        return profile.documents.some(
          (document) => document.kind === PlatformDocumentKind.MAIN_CV
        );
      case ProfileCompletenessFragment.SKILLS:
        return profile.skills.length > 0;
      case ProfileCompletenessFragment.PREFERENCES:
        return (
          profile.preferredLocations.length > 0 &&
          Boolean(profile.workingModel) &&
          (profile.salaryMin != null || profile.salaryMax != null)
        );
      default:
        return false;
    }
  }
}
